import { ValidationResult, UrlMapping, ValidationError } from '../types'
import { isValidUrl, parseSpreadsheet } from 'shared/utils'

/**
 * Validate spreadsheet data for URL swap using parseSpreadsheet utility
 * Checks for required columns and validates each row
 */
export function validateSpreadsheetData(rawData: string[][]): ValidationResult {
  // Need at least 2 rows (header + 1 data row)
  if (rawData.length < 2) {
    return {
      isValid: false,
      data: [],
      errors: [
        {
          row: 0,
          message:
            'Spreadsheet must contain at least a header row and one data row',
        },
      ],
    }
  }

  const errors: ValidationError[] = []

  try {
    const result = parseSpreadsheet<UrlMapping>(rawData, {
      // Find header row with old/new url columns (case-insensitive)
      findHeaderRow: (rows) => {
        return rows.findIndex(
          (row) =>
            row.some((cell) => /old.?url/i.test(cell)) &&
            row.some((cell) => /new.?url/i.test(cell)),
        )
      },

      // Map headers to snake_case format (old_url, new_url)
      // Skip columns that don't match our required headers
      mapHeader: (header) => {
        const normalized = header.toLowerCase().replace(/[_\s-]/g, '')
        if (
          normalized === 'oldurl' ||
          normalized === 'sourceurl' ||
          normalized === 'old'
        ) {
          return 'old_url'
        }
        if (
          normalized === 'newurl' ||
          normalized === 'targeturl' ||
          normalized === 'destinationurl' ||
          normalized === 'new'
        ) {
          return 'new_url'
        }
        // Skip columns we don't recognize
        return false
      },

      // Transform and validate each cell value
      transformValue: (value, key, rowIndex) => {
        const trimmed = value.trim()
        const displayRow = rowIndex + 1 // 1-indexed for user display

        // Check for empty values
        if (!trimmed) {
          errors.push({
            row: displayRow,
            message: `Missing ${key.replace('_', ' ')}`,
          })
          return '' // Return empty string so we still include the row
        }

        // Validate URL format
        if (!isValidUrl(trimmed)) {
          errors.push({
            row: displayRow,
            message: `Invalid ${key.replace('_', ' ')} format: ${trimmed}`,
          })
        }

        return trimmed
      },

      // Skip completely empty rows
      skipEmptyRows: true,
    })

    // Check if we found the required headers
    if (result.headerRowIndex === -1) {
      return {
        isValid: false,
        data: [],
        errors: [
          {
            row: 0,
            message:
              "Could not find required columns: 'old url' and 'new url' (or similar variations)",
          },
        ],
      }
    }

    // Check if we got valid data
    if (result.data.length === 0) {
      return {
        isValid: false,
        data: [],
        errors: [
          {
            row: 0,
            message: 'No valid data rows found in spreadsheet',
          },
        ],
      }
    }

    return {
      isValid: errors.length === 0,
      data: result.data,
      errors,
    }
  } catch (error) {
    // Handle parseSpreadsheet errors
    const errorMessage = error instanceof Error ? error.message : String(error)
    return {
      isValid: false,
      data: [],
      errors: [
        {
          row: 0,
          message: `Failed to parse spreadsheet: ${errorMessage}`,
        },
      ],
    }
  }
}
